import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserLayout from '../components/UserLayout';
import Card, { CardHeader, CardBody } from '../components/Card';
import Button from '../components/Button';
import { BellIcon, CheckIcon, TrashIcon, AlertCircleIcon, ClockIcon, FileTextIcon, MessageSquareIcon, CreditCardIcon, InfoIcon, CheckCircleIcon, XCircleIcon, MailIcon, PhoneIcon, SmartphoneIcon } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
const NotificationsPage: React.FC = () => {
  const {
    getNotificationsForCurrentUser,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    deleteNotification,
    unreadCount
  } = useNotifications();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'unread' | 'read'>('all');
  const navigate = useNavigate();
  useEffect(() => {
    loadNotifications();
  }, []);
  const loadNotifications = () => {
    const userNotifications = getNotificationsForCurrentUser();
    setNotifications(userNotifications);
  };
  const handleMarkAsRead = (id: string) => {
    markAsRead(id);
    loadNotifications();
  };
  const handleMarkAllAsRead = () => {
    markAllAsRead();
    loadNotifications();
  };
  const handleArchive = (id: string) => {
    archiveNotification(id);
    loadNotifications();
  };
  const handleDelete = (id: string) => {
    deleteNotification(id);
    loadNotifications();
  };
  const handleNavigateToApplication = (referenceNumber: string) => {
    navigate(`/track-application/${referenceNumber}`);
  };
  const filteredNotifications = notifications.filter(notification => {
    if (activeFilter === 'unread') return notification.status === 'unread';
    if (activeFilter === 'read') return notification.status === 'read';
    return true;
  });
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'application_submitted':
        return <FileTextIcon className="h-5 w-5 text-blue-500" data-id="element-5684" />;
      case 'payment_completed':
        return <CreditCardIcon className="h-5 w-5 text-green-500" data-id="element-5685" />;
      case 'status_updated':
        return <InfoIcon className="h-5 w-5 text-purple-500" data-id="element-5686" />;
      case 'action_required':
        return <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-5687" />;
      case 'application_incomplete':
        return <XCircleIcon className="h-5 w-5 text-orange-500" data-id="element-5688" />;
      case 'application_approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-5689" />;
      case 'application_rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-5690" />;
      case 'document_uploaded':
        return <FileTextIcon className="h-5 w-5 text-indigo-500" data-id="element-5691" />;
      case 'comment_added':
        return <MessageSquareIcon className="h-5 w-5 text-teal-500" data-id="element-5692" />;
      case 'escalation':
        return <ClockIcon className="h-5 w-5 text-amber-500" data-id="element-5693" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" data-id="element-5694" />;
    }
  };
  const getChannelIcons = (channels: string[]) => {
    return <div className="flex space-x-1 mt-1" data-id="element-5695">
        {channels.includes('email') && <span title="Email notification sent" className="text-gray-400" data-id="element-5696">
            <MailIcon size={14} data-id="element-5697" />
          </span>}
        {channels.includes('sms') && <span title="SMS notification sent" className="text-gray-400" data-id="element-5698">
            <SmartphoneIcon size={14} data-id="element-5699" />
          </span>}
        {channels.includes('in_app') && <span title="In-app notification" className="text-gray-400" data-id="element-5700">
            <BellIcon size={14} data-id="element-5701" />
          </span>}
      </div>;
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInSecs = Math.floor(diffInMs / 1000);
    const diffInMins = Math.floor(diffInSecs / 60);
    const diffInHours = Math.floor(diffInMins / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInSecs < 60) {
      return 'Just now';
    } else if (diffInMins < 60) {
      return `${diffInMins} minute${diffInMins > 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };
  return <UserLayout data-id="element-5702">
      <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen" data-id="element-5703">
        <div className="max-w-7xl mx-auto" data-id="element-5704">
          <div className="mb-6 flex items-center justify-between" data-id="element-5705">
            <div data-id="element-5706">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-5707">
                Notifications
              </h1>
              <p className="text-gray-600 mt-1" data-id="element-5708">
                Stay updated with the latest information about your applications
                and services.
              </p>
            </div>
            {unreadCount > 0 && <Button variant="outline" onClick={handleMarkAllAsRead} className="flex items-center" data-id="element-5709">
                <CheckIcon className="mr-2 h-4 w-4" data-id="element-5710" />
                Mark All as Read
              </Button>}
          </div>
          <div className="mb-6" data-id="element-5711">
            <div className="flex space-x-2 border-b border-gray-200" data-id="element-5712">
              <button onClick={() => setActiveFilter('all')} className={`px-4 py-2 text-sm font-medium ${activeFilter === 'all' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5713">
                All Notifications
              </button>
              <button onClick={() => setActiveFilter('unread')} className={`px-4 py-2 text-sm font-medium flex items-center ${activeFilter === 'unread' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5714">
                Unread
                {unreadCount > 0 && <span className="ml-2 bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full" data-id="element-5715">
                    {unreadCount}
                  </span>}
              </button>
              <button onClick={() => setActiveFilter('read')} className={`px-4 py-2 text-sm font-medium ${activeFilter === 'read' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5716">
                Read
              </button>
            </div>
          </div>
          {filteredNotifications.length === 0 ? <Card data-id="element-5717">
              <CardBody className="p-8 text-center" data-id="element-5718">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100" data-id="element-5719">
                  <BellIcon className="h-6 w-6 text-green-600" data-id="element-5720" />
                </div>
                <h3 className="mt-2 text-lg font-medium text-gray-900" data-id="element-5721">
                  No notifications
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-5722">
                  {activeFilter === 'unread' ? "You're all caught up! You have no unread notifications." : activeFilter === 'read' ? "You don't have any read notifications yet." : "You don't have any notifications yet."}
                </p>
              </CardBody>
            </Card> : <div className="space-y-4" data-id="element-5723">
              {filteredNotifications.map(notification => <Card key={notification.id} className={`transition-colors ${notification.status === 'unread' ? 'bg-green-50 border-l-4 border-green-500' : 'bg-white'}`} data-id="element-5724">
                  <CardBody className="p-4" data-id="element-5725">
                    <div className="flex items-start justify-between" data-id="element-5726">
                      <div className="flex items-start space-x-3" data-id="element-5727">
                        <div className="flex-shrink-0 mt-1" data-id="element-5728">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div data-id="element-5729">
                          <h3 className={`text-sm font-medium ${notification.status === 'unread' ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-5730">
                            {notification.title}
                          </h3>
                          <p className="mt-1 text-sm text-gray-600" data-id="element-5731">
                            {notification.message}
                          </p>
                          {notification.referenceNumber && <button onClick={() => handleNavigateToApplication(notification.referenceNumber)} className="mt-2 text-xs text-green-600 hover:text-green-800 flex items-center" data-id="element-5732">
                              <FileTextIcon className="h-3 w-3 mr-1" data-id="element-5733" />
                              View Application: {notification.referenceNumber}
                            </button>}
                          {getChannelIcons(notification.channels)}
                        </div>
                      </div>
                      <div className="flex flex-col items-end" data-id="element-5734">
                        <span className="text-xs text-gray-500" data-id="element-5735">
                          {formatDate(notification.createdAt)}
                        </span>
                        <div className="mt-2 flex space-x-2" data-id="element-5736">
                          {notification.status === 'unread' && <button onClick={() => handleMarkAsRead(notification.id)} className="text-gray-400 hover:text-green-600" title="Mark as read" data-id="element-5737">
                              <CheckIcon className="h-4 w-4" data-id="element-5738" />
                            </button>}
                          <button onClick={() => handleDelete(notification.id)} className="text-gray-400 hover:text-red-600" title="Delete notification" data-id="element-5739">
                            <TrashIcon className="h-4 w-4" data-id="element-5740" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>)}
            </div>}
        </div>
      </div>
    </UserLayout>;
};
export default NotificationsPage;